import React, { FC, useMemo, useState } from 'react';
import type { WebsiteHeaderLayout_PhotoBackground } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex, useToast } from '@withjoy/joykit';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { EventNameNavForStandaloneHeader, EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { BrannanNavigation, EventPageType } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteEventDesign } from '../../../../layout.types';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';

export type VideoBackgroundHotfixLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_PhotoBackground;
    showSkeleton?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
    videoId?: string;
    mobileVideoId: string | undefined;
    fallbackImageUrl?: string;
  } & EventNameNavForStandaloneHeaderProps
>;

// This is a mock implementation for a demo. Will be replaced with the actual video background component.
type VideoBackgroundProps = Readonly<{
  videoId?: string;
  fallbackImageUrl?: string;
  onError?: () => void;
}>;

const VideoBackground = ({ videoId, fallbackImageUrl, onError }: VideoBackgroundProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const { tq } = useTranslation('guestKit');
  const [error, setError] = useState(false);

  const handleIframeLoad = useEventCallback(() => {
    setIsLoading(false);
  });

  const handleIframeError = useEventCallback(() => {
    setIsLoading(false);
    setError(true);
    onError?.();
  });

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height="100%"
      overflow="hidden"
      background="#000"
    >
      <Box
        as="iframe"
        title="Vimeo Background"
        src={`https://player.vimeo.com/video/${videoId}?background=1&autoplay=1&loop=1&muted=1&playsinline=1`}
        allow="autoplay; fullscreen; picture-in-picture"
        position="absolute"
        top="50%"
        left="50%"
        border="none"
        outline="none"
        transform="translate(-50%, -50%)"
        opacity={isLoading || error ? 0 : 1}
        transition="opacity 0.3s ease-in-out"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        width="100vw"
        height="100vh"
        minWidth="177.78vh"
        minHeight="56.25vw"
      />

      {(isLoading || error) && fallbackImageUrl && (
        <Box
          as="img"
          src={fallbackImageUrl}
          alt={tq`Video background fallback`}
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          objectFit="cover"
          zIndex={1}
        />
      )}
    </Box>
  );
};

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;

export const VideoBackgroundHotfixLayout: FC<VideoBackgroundHotfixLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    videoId,
    fallbackImageUrl,
    mobileVideoId,
    ...restProps
  } = props;
  const { toast } = useToast();
  const { tq } = useTranslation('guestKit');

  const pageIndex = useMemo(() => pages?.findIndex(p => p.pageSlug === pageSlug), [pages, pageSlug]);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && currentPageType !== EventPageType.welcome && pageIndex !== 0;
  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : { height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : '100vh', aspectRatio: `${CONTAINER_ASPECT_RATIO}` },
    [isCompactView, isInlineEditing, renderedContainerHeight],
  );

  const [currentVideoId] = useResponsive({
    values: {
      mobile: mobileVideoId || videoId,
      tablet: videoId,
    },
  });

  // If the current video id is not defined, use the video id
  // This is to handle the case where the video id is not defined for the mobile screen
  const videoIdToUse = currentVideoId || videoId;

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const onError = useEventCallback(() => {
    toast(tq`Error loading video background`, { icon: <WarningTriangle /> });
  });

  return (
    <Flex
      position="relative"
      zIndex="docked"
      width="100%"
      aspectRatio={aspectRatio}
      height={height}
      //  minHeight ensures we will render header photo for non-welcome pages on mobile screen dimensions (EventMenu is positioned fixed so added a minHeight = 24px top padding + 48px menu height + 24px bottom padding)
      minHeight={pxToRem(96)}
      overflow="hidden"
    >
      <>
        <VideoBackground
          videoId={videoIdToUse}
          fallbackImageUrl={fallbackImageUrl}
          onError={onError}
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          zIndex={2}
        >
          <EventNameNavForStandaloneHeader
            eventDisplayName={websiteHeader.eventDisplayName}
            colors={websiteHeader.headerPhoto.colors}
            headerTextData={websiteHeader.headerPhoto.eventName}
            isCompactView={isCompactView}
            currentPageType={currentPageType}
            handleEditTextClick={handleEditTextClick}
            isContentEditable={isInlineEditing}
            editableContentRef={editableContentRef}
            forceRenderViewIn={forceRenderViewIn}
            scaleValue={scaleValue}
            pages={pages}
            pageSlug={pageSlug}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
            {...restProps}
          />
        </Box>
      </>
    </Flex>
  );
};

VideoBackgroundHotfixLayout.displayName = 'VideoBackgroundHotfixLayout';
